import React from 'react'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import BarChartIcon from '@mui/icons-material/BarChart'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'

import { isUnread, useNotificationsStore } from './stores/notificationsStore'
import { relativeTime } from './targets'

const SOURCE_ICONS = {
    CRM: <ViewKanbanIcon fontSize="small" />,
    DEALS: <DirectionsCarIcon fontSize="small" />,
    LEADS: <PersonAddAltIcon fontSize="small" />,
    EMAIL: <EmailOutlinedIcon fontSize="small" />,
    LISTINGS: <BarChartIcon fontSize="small" />,
    SYSTEM: <InfoOutlinedIcon fontSize="small" />,
}

export const NotificationRow = ({ notification }) => {
    const markRead = useNotificationsStore((s) => s.markRead)
    const archive = useNotificationsStore((s) => s.archive)
    const unarchive = useNotificationsStore((s) => s.unarchive)
    const select = useNotificationsStore((s) => s.select)
    const selectedId = useNotificationsStore((s) => s.selectedId)

    const unread = isUnread(notification)
    const selected = selectedId === notification.id
    const isArchived = notification.status === 'ARCHIVED'

    // Opening a notification keeps you in the dashboard. Navigating straight to
    // the target on click sent people to records that may not exist (and, for a
    // SYSTEM notice, to nowhere at all); the deep link is now an explicit
    // action in the detail pane instead of a side effect of reading.
    const onOpen = () => {
        if (unread) markRead(notification.id)
        select(notification.id)
    }

    const onArchive = (event) => {
        // The whole row is clickable; without this, archiving would also open
        // the notification being filed away.
        event.stopPropagation()
        archive(notification.id)
    }

    const onUnarchive = (event) => {
        event.stopPropagation()
        unarchive(notification.id)
    }

    return (
        <div
            className={`notif-row clickable${unread ? ' unread' : ''}${selected ? ' selected' : ''}`}
            onClick={onOpen}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}
        >
            <span className="notif-dot" aria-hidden={!unread}>{unread ? '●' : ''}</span>
            <span className={`notif-icon source-${notification.source.toLowerCase()}`}>
                {SOURCE_ICONS[notification.source] || SOURCE_ICONS.SYSTEM}
            </span>

            <div className="notif-main">
                <div className="notif-title">{notification.title}</div>
                {notification.body && <div className="notif-body">{notification.body}</div>}
            </div>

            <time className="notif-time" dateTime={notification.created_at}>
                {relativeTime(notification.created_at)}
            </time>

            {isArchived ? (
                <button
                    className="notif-archive"
                    onClick={onUnarchive}
                    title="Unarchive"
                    aria-label={`Unarchive: ${notification.title}`}
                >
                    <UnarchiveOutlinedIcon fontSize="small" />
                </button>
            ) : (
                <button
                    className="notif-archive"
                    onClick={onArchive}
                    title="Archive"
                    aria-label={`Archive: ${notification.title}`}
                >
                    <ArchiveOutlinedIcon fontSize="small" />
                </button>
            )}
        </div>
    )
}
